import React, { useEffect, useRef } from 'react';
import { StaticImage } from 'gatsby-plugin-image';
import styled from 'styled-components';
import { srConfig } from '@config';
import sr from '@utils/sr';
import { usePrefersReducedMotion } from '@hooks';

const StyledAboutSection = styled.section`
  max-width: 900px;

  .inner {
    display: block;

    @media (min-width: 769px) {
      display: grid;
      grid-template-columns: 3fr 2fr;
      grid-gap: 50px;
    }
  }
`;
const StyledText = styled.div`
  ul.skills-list {
    display: grid;
    grid-template-columns: repeat(2, minmax(140px, 200px));
    grid-gap: 0 10px;
    padding: 0;
    margin: 20px 0 0 0;
    overflow: hidden;
    list-style: none;

    li {
      position: relative;
      margin-bottom: 10px;
      padding-left: 20px;
      font-family: var(--font-mono);
      font-size: var(--fz-xs);

      &:before {
        content: '▹';
        position: absolute;
        left: 0;
        color: var(--green);
        font-size: var(--fz-sm);
        line-height: 12px;
      }
    }
  }
`;

const StyledPic = styled.div`
  position: right;
  max-width: 300px;
  margin: 50px auto 0;
  width: 70%;

  @media (min-width: 769px) {
    margin: 0;
    width: auto;
  }

  .wrapper {
    ${({ theme }) => theme.mixins.boxShadow};
    display: block;
    position: relative;
    width: 100%;
    border-radius: var(--border-radius);
    border: 2px solid var(--lightest-navy);
    overflow: hidden;
    transition: var(--transition);

    &:hover,
    &:focus {
      outline: 0;
      border-color: var(--green);
      box-shadow: 0 0 20px var(--green-glow);

      .img {
        filter: none;
      }
    }

    .img {
      border-radius: var(--border-radius);
      filter: brightness(0.9);
      transition: var(--transition);
    }

    &:before {
      content: '';
      display: block;
      position: absolute;
      top: 0;
      left: 0;
      width: 100%;
      height: 100%;
      border-radius: var(--border-radius);
      background-color: var(--navy);
      opacity: 0.3;
      transition: var(--transition);
      z-index: 1;
    }

    &:hover:before {
      opacity: 0;
    }
  }
`;

const GalleryWrapper = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 10px;
  justify-content: flex-start;
  align-items: center;
  margin: 10px 0;

  @media (min-width: 769px) {
    gap: 20px;
  }

  ${StyledPic} {
    margin: 0;
    width: auto;
    max-width: none;
  }
`;

const ImageGallery = () => (
  <GalleryWrapper>
    <StyledPic>
      <StaticImage
        className="img"
        src="../../images/oscp.png"
        width={80}
        quality={95}
        layout="fixed"
        formats={['AUTO', 'WEBP', 'AVIF', 'jpg']}
        alt="OSCP"
      />
    </StyledPic>

    <StyledPic>
      <StaticImage
        className="img"
        src="../../images/crtp.png"
        width={80}
        quality={95}
        layout="fixed"
        formats={['AUTO', 'WEBP', 'AVIF', 'jpg']}
        alt="Cert 1"
      />
    </StyledPic>
  </GalleryWrapper>
);

const About = () => {
  const revealContainer = useRef(null);
  const prefersReducedMotion = usePrefersReducedMotion();

  useEffect(() => {
    if (prefersReducedMotion) {
      return;
    }

    sr.reveal(revealContainer.current, srConfig());
  }, []);

  const skills = [
    'Offensive Security',
    'Network Assessment',
    'Web App Testing',
    'Penetration Testing',
    'Red Teaming',
    'Burp Suite',
  ];

  return (
    <StyledAboutSection id="about" ref={revealContainer}>
      <h2 className="numbered-heading">About Me</h2>

      <div className="inner">
        <StyledText>
          <div>
            <p>
              Hello! I'm Daniel, a Product Security Manager at{' '}
              <a href="https://pentest-tools.com/">Pentest-Tools.com</a> based in Bucharest,
              Romania.
            </p>

            <p>
              I audit network infrastructures and web applications for vulnerabilities, delivering
              actionable penetration test reports. My goal is to help organizations strengthen their
              defenses and reduce the risk of compromise.
            </p>
            <p>
              I hold the{' '}
              <a href="https://www.credly.com/badges/255bd576-6711-4bc7-90ee-627ff28e4f6c">OSCP</a>{' '}
              and{' '}
              <a href="https://www.credential.net/78f8fa8e-9692-4076-92ab-80de07ea5869#acc.6NXb3Ym2">
                CRTP
              </a>{' '}
              certifications.
            </p>
            <ImageGallery />
            <p>
              I'm also an approved cybersecurity{' '}
              <a href="https://dnsc.ro/vezi/document/lista-aasc-pf-la-data-de-11-06-2024">
                auditor
              </a>{' '}
              under Romania's National Cyber Security Directorate (DNSC).
            </p>

            <p>
              I hold both a bachelor's and a master's degree in computer engineering from the{' '}
              <a href="https://upb.ro/">University Politehnica of Bucharest</a>.
            </p>

            <p>Here are some areas and tools I work with:</p>
          </div>

          <ul className="skills-list">
            {skills && skills.map((skill, i) => <li key={i}>{skill}</li>)}
          </ul>
        </StyledText>

        <StyledPic>
          <div className="wrapper">
            <StaticImage
              className="img"
              src="../../images/daniel.jpg"
              width={500}
              quality={95}
              formats={['AUTO', 'WEBP', 'AVIF', 'jpg']}
              alt="Headshot"
            />
          </div>
        </StyledPic>
      </div>
    </StyledAboutSection>
  );
};

export default About;
